/**
 * Client for the human-handoff agent-profile endpoints
 * (`/api/agent-profiles`). Thin wrappers over `apiRequest`; the wire shape is
 * already camelCase, so no key transforms. Workspace scope rides the session
 * token (the `WorkspaceProvider` swaps it per slug), so the paths carry no slug.
 */

import { apiRequest } from "../agents/api/client";

export type Availability = "available" | "unavailable";

/** Weekly shift — local wall-clock strings in `timezone`, NOT UTC. A missing
 *  day = off; the whole object null = always in-shift. v1: ≤1 interval/day. */
export type WorkingHours = Partial<
  Record<
    "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun",
    Array<{ start: string; end: string }>
  >
>;

export interface AgentProfile {
  id: string;
  workspaceId: string;
  userId: string;
  email: string;
  displayName: string | null;
  availability: Availability;
  /** When availability last changed — "Available for 2h" in the roster. */
  statusChangedAt: string | null;
  concurrencyCap: number;
  skills: string[];
  /** Weekly shift + IANA zone; null = always in-shift. */
  workingHours: WorkingHours | null;
  timezone: string | null;
  /** Live count of open handoffs assigned to this agent (computed server-side). */
  activeConversations: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAgentProfileBody {
  userId: string;
  displayName?: string | null;
  availability?: Availability;
  concurrencyCap?: number;
  skills?: string[];
  workingHours?: WorkingHours | null;
  timezone?: string | null;
}

export interface UpdateAgentProfileBody {
  displayName?: string | null;
  availability?: Availability;
  concurrencyCap?: number;
  skills?: string[];
  workingHours?: WorkingHours | null;
  timezone?: string | null;
}

export const agentProfileKeys = {
  list: (slug: string) => ["agent-profiles", slug] as const,
};

export function listAgentProfiles(): Promise<{ items: AgentProfile[] }> {
  return apiRequest<{ items: AgentProfile[] }>("GET", "/api/agent-profiles");
}

export function createAgentProfile(body: CreateAgentProfileBody): Promise<AgentProfile> {
  return apiRequest<AgentProfile>("POST", "/api/agent-profiles", body);
}

export function updateAgentProfile(
  id: string,
  body: UpdateAgentProfileBody,
): Promise<AgentProfile> {
  return apiRequest<AgentProfile>("PATCH", `/api/agent-profiles/${id}`, body);
}

// ── Live monitoring board + takeover / handback ──────────────────────────

export type SessionStatus = "ai_handling" | "queued" | "human_handling" | "resolved";

export interface LiveSession {
  id: string;
  title: string | null;
  status: SessionStatus;
  channel: string;
  lastActivityAt: string;
  activeHandoffId: string | null;
  reason: string | null;
  /** The session's contact (null for anonymous/legacy) — addresses standing steers. */
  contactId: string | null;
  contact: { displayName: string | null; email: string | null } | null;
  /** The human currently handling it (My chats / Unresolved), when claimed. */
  claimedByUserId: string | null;
  claimedBy: { displayName: string | null; avatarUrl: string | null } | null;
  /** Routing team of the open handoff (null = general queue), denormalized. */
  teamId: string | null;
  teamName: string | null;
  /** Assigned but not yet replied to — the "New" pill in My chats. */
  awaitingReply: boolean;
  /** Last customer-visible message text — the row's preview snippet. */
  lastMessagePreview: string | null;
}

export interface LiveFilter {
  /** Session statuses to include (e.g. `["queued"]` for the Queue tab). */
  status?: SessionStatus[];
  /** Only sessions whose open handoff is claimed by the caller (My chats). */
  mine?: boolean;
}

export const liveKeys = {
  list: (slug: string) => ["handoff-live", slug] as const,
  filtered: (slug: string, f: LiveFilter) => ["handoff-live", slug, f] as const,
};

export function listLiveSessions(filter: LiveFilter = {}): Promise<{ items: LiveSession[] }> {
  return apiRequest<{ items: LiveSession[] }>("GET", "/api/handoffs/live", undefined, {
    ...(filter.status?.length ? { status: filter.status.join(",") } : {}),
    ...(filter.mine ? { mine: "true" } : {}),
  });
}

export function takeOverSession(sessionId: string): Promise<{ id: string; sessionId: string }> {
  return apiRequest("POST", "/api/handoffs/takeover", { sessionId });
}

export function claimHandoff(handoffId: string): Promise<{ ok: true }> {
  return apiRequest("POST", `/api/handoffs/${handoffId}/claim`, {});
}

export function handbackHandoff(handoffId: string, note?: string): Promise<{ ok: true }> {
  return apiRequest("POST", `/api/handoffs/${handoffId}/handback`, note ? { note } : {});
}

/**
 * Resolve (close) a conversation: marks the session `resolved` and closes the
 * handoff. Distinct from hand-back — the AI does NOT resume immediately; a
 * later customer message re-opens the session to `ai_handling`.
 */
export function resolveHandoff(handoffId: string, note?: string): Promise<{ ok: true }> {
  return apiRequest("POST", `/api/handoffs/${handoffId}/resolve`, note ? { note } : {});
}

/**
 * Resolve a conversation DIRECTLY by session — no claim/take-over needed.
 * Takes no ownership and fires no join announcement; works in every open
 * status (ai_handling / queued / human_handling).
 */
export function resolveSession(sessionId: string, note?: string): Promise<{ ok: true }> {
  return apiRequest("POST", "/api/handoffs/resolve", note ? { sessionId, note } : { sessionId });
}

export function sendHumanMessage(
  sessionId: string,
  text: string,
): Promise<{ id: string; seq: number }> {
  return apiRequest("POST", `/api/sessions/${sessionId}/human-messages`, { text });
}

/**
 * Post an internal note — an operator-private annotation on the conversation.
 * The customer and the AI never see it; only the dashboard transcript renders
 * it (as a `role: "internal_note"` message). Allowed in ANY session status.
 */
export function createNote(sessionId: string, text: string): Promise<{ id: string; seq: number }> {
  return apiRequest("POST", `/api/sessions/${sessionId}/notes`, { text });
}

// ── Decisions inbox (request_decision) ───────────────────────────────────

export interface InboxDecision {
  id: string;
  sessionId: string;
  subjectKey: string | null;
  questionText: string;
  createdAt: string;
  contact: { displayName: string | null; email: string | null } | null;
}

export const decisionKeys = {
  list: (slug: string) => ["decisions", slug] as const,
};

export function listDecisions(): Promise<{ items: InboxDecision[] }> {
  return apiRequest<{ items: InboxDecision[] }>("GET", "/api/decisions");
}

export function ruleDecision(
  id: string,
  outcome: "accepted" | "denied",
  note?: string,
): Promise<{ ok: true }> {
  return apiRequest("POST", `/api/decisions/${id}/rule`, note ? { outcome, note } : { outcome });
}

// ── Live micro-instructions (session steers) ─────────────────────────────

export interface SessionSteer {
  id: string;
  text: string;
  /** null = all agents; set = one worker. */
  agentId: string | null;
  anchorMessageId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export const steerKeys = {
  list: (slug: string, sessionId: string) => ["steers", slug, sessionId] as const,
};

export function listSteers(sessionId: string): Promise<{ items: SessionSteer[] }> {
  return apiRequest<{ items: SessionSteer[] }>("GET", `/api/sessions/${sessionId}/steers`);
}

export function createSteer(
  sessionId: string,
  body: { text: string; agentId?: string | null; anchorMessageId?: string | null },
): Promise<SessionSteer> {
  return apiRequest<SessionSteer>("POST", `/api/sessions/${sessionId}/steers`, body);
}

export function updateSteer(sessionId: string, id: string, text: string): Promise<SessionSteer> {
  return apiRequest<SessionSteer>("PATCH", `/api/sessions/${sessionId}/steers/${id}`, { text });
}

export function deleteSteer(sessionId: string, id: string): Promise<void> {
  return apiRequest("DELETE", `/api/sessions/${sessionId}/steers/${id}`);
}

export function applySteers(sessionId: string): Promise<{ ok: true }> {
  return apiRequest("POST", `/api/sessions/${sessionId}/steers/apply`, {});
}

// ── Contact-level (standing) micro-instructions ──────────────────────────

export const contactSteerKeys = {
  list: (slug: string, contactId: string) => ["contact-steers", slug, contactId] as const,
};

export function listContactSteers(contactId: string): Promise<{ items: SessionSteer[] }> {
  return apiRequest<{ items: SessionSteer[] }>("GET", `/api/contacts/${contactId}/steers`);
}

export function createContactSteer(
  contactId: string,
  body: { text: string; agentId?: string | null },
): Promise<SessionSteer> {
  return apiRequest<SessionSteer>("POST", `/api/contacts/${contactId}/steers`, body);
}

export function updateContactSteer(
  contactId: string,
  id: string,
  text: string,
): Promise<SessionSteer> {
  return apiRequest<SessionSteer>("PATCH", `/api/contacts/${contactId}/steers/${id}`, { text });
}

export function deleteContactSteer(contactId: string, id: string): Promise<void> {
  return apiRequest("DELETE", `/api/contacts/${contactId}/steers/${id}`);
}

// ── Workspace handoff settings (transparency + join announcement) ─────────

export interface JoinAnnouncement {
  enabled: boolean;
  /** `{name}` is substituted with the joining agent's name. */
  template: string;
}

/** Routing & assignment settings (teams-routing LLD §4). Only the strategy /
 *  capacity knobs are enforced in v1; after-hours + reassign persist for the
 *  timer-based slice. */
export interface RoutingSettings {
  autoAssignStrategy: "round_robin" | "balanced_load" | "claim";
  maxOpenPerAgent: number;
  stopAtCapacity: boolean;
  afterHours: "queue" | "reply" | "oncall";
  reassignOnNoResponse: boolean;
}

/** CSAT post-resolution survey config (FR-N-3). Sent immediately on resolve. */
export interface CsatConfig {
  enabled: boolean;
  scale: "stars" | "thumbs";
  question: string;
  followUp: string | null;
  /** Stars: minimum score counted as "happy" in CSAT% (default 4 → 4–5★). */
  starsHappyFrom: number;
  /** Thumbs: labels on the two survey buttons. */
  thumbsUpLabel: string;
  thumbsDownLabel: string;
}

export interface HandoffSettings {
  transparency: "badge" | "silent";
  /** Does Resolve close the chat for good, or reopen on the customer's next
   *  message (feature-narrative FR-A-1). Defaults to `reopen`. */
  resolveBehavior: "reopen" | "terminal";
  joinAnnouncement: JoinAnnouncement;
  /** "Back with the AI" marker on handback — same shape as joinAnnouncement. */
  handbackAnnouncement: JoinAnnouncement;
  routing: RoutingSettings;
  csat: CsatConfig;
}

/** CSAT rollups for the settings screen's stat tiles. */
export interface CsatStats {
  from: string;
  to: string;
  sent: number;
  responded: number;
  responseRate: number | null;
  csatPct: number | null;
}

/** Preset window (`{window: "60d"}`) or custom range (`{from, to}` — YYYY-MM-DD). */
export type CsatStatsRange = { window: string } | { from: string; to: string };

export const csatKeys = {
  stats: (slug: string, range: CsatStatsRange) => ["csat-stats", slug, range] as const,
};

export function getCsatStats(range: CsatStatsRange): Promise<CsatStats> {
  return apiRequest<CsatStats>("GET", "/api/csat/stats", undefined, { ...range });
}

export const handoffSettingsKeys = {
  get: (slug: string) => ["handoff-settings", slug] as const,
};

export function getHandoffSettings(): Promise<HandoffSettings> {
  return apiRequest<HandoffSettings>("GET", "/api/handoff-settings");
}

export function updateHandoffSettings(body: Partial<HandoffSettings>): Promise<HandoffSettings> {
  return apiRequest<HandoffSettings>("PATCH", "/api/handoff-settings", body);
}

// ── Routing teams (helpdesk teams the AI routes handoffs to) ──────────────

export interface TeamMember {
  agentProfileId: string;
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  availability: Availability;
}

export interface Team {
  id: string;
  workspaceId: string;
  name: string;
  /** Routing slug the AI picks in `handoff_to_human` (unique per workspace). */
  tag: string;
  /** Skill keywords the AI matches the conversation against. */
  description: string;
  members: TeamMember[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamBody {
  name: string;
  tag: string;
  description?: string;
  agentProfileIds?: string[];
}

export interface UpdateTeamBody {
  name?: string;
  tag?: string;
  description?: string;
}

export const teamKeys = {
  list: (slug: string) => ["teams", slug] as const,
};

export function listTeams(): Promise<{ items: Team[] }> {
  return apiRequest<{ items: Team[] }>("GET", "/api/teams");
}

export function createTeam(body: CreateTeamBody): Promise<Team> {
  return apiRequest<Team>("POST", "/api/teams", body);
}

export function updateTeam(id: string, body: UpdateTeamBody): Promise<Team> {
  return apiRequest<Team>("PATCH", `/api/teams/${id}`, body);
}

export function deleteTeam(id: string): Promise<void> {
  return apiRequest("DELETE", `/api/teams/${id}`);
}

export function setTeamMembers(id: string, agentProfileIds: string[]): Promise<Team> {
  return apiRequest<Team>("PUT", `/api/teams/${id}/members`, { agentProfileIds });
}
